<template>
  <InputRow v-model="selected" name="Flags">
    <MultiSelect
      ref="multiselect"
      v-model="selected"
      track-by="id"
      filter
      option-label="name"
      class="w-100"
      display="chip"
      auto-filter-focus
      :options="options"
      @change="toggle"
    >
      <template #option="{ option }">
        <span>
          <span
            v-if="
              te(`dynamic.${option.abbreviationId}`) === false &&
              te(`dynamic.${option.displayNameId}`) === false
            "
          >
            {{ option.name }}
          </span>
          <span v-if="te(`dynamic.${option.displayNameId}`)">{{
            t(`dynamic.${option.displayNameId}`)
          }}</span>
          <span v-if="te(`dynamic.${option.abbreviationId}`)"
            >( {{ t(`dynamic.${option.abbreviationId}`) }} )</span
          >
          <span
            >[{{ t("global.id") }}: {{ option.id }}
            <span v-if="option.type === FlagTypes.mailDistribution"> 📧 </span>]</span
          >
          <span> [{{ option?.count }}x]</span>
        </span>
      </template>
      <template #value="{ value }">
        <div
          v-for="flag in value"
          :key="flag.id"
          class="flex p-1 border-round"
          :style="hasOwnColor(flag)"
        >
          <div class="inline mr-1">
            <span>
              {{
                te(`dynamic.${flag.displayNameId}`)
                  ? t(`dynamic.${flag.displayNameId}`)
                  : te(`dynamic.${flag.abbreviationId}`)
                    ? t(`dynamic.${flag.abbreviationId}`)
                    : flag.name
              }}
            </span>
            <span>
              [{{ t("global.id") + ":" + flag.id }}
              <span v-if="flag.type === FlagTypes.mailDistribution"> 📧 </span>]
            </span>
          </div>
          <div class="inline multiselect__tag-icon" @click.stop="remove(flag)">
            <i class="fas fa-times-circle"></i>
          </div>
        </div>
      </template>
    </MultiSelect>
  </InputRow>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import MultiSelect from "primevue/multiselect";
import { computed, ref } from "vue";
import FlagsFilter from "../filterImplementations/FlagsFilter";
import refilterEventsWithout from "./RefilterEventsWithout";
import { getContrastColor } from "../../../../functions/colorHelper";
import { FlagTypes } from "@/graphql";

export type OptionType = {
  id: number;
  name: string;
  color: string;
  count?: number;
  type: string;
  systemFlag?: boolean;
};

defineEmits(["hide"]);
const filteredEvents = refilterEventsWithout((filter) => !(filter instanceof FlagsFilter));
const multiselect = ref<InstanceType<typeof MultiSelect>>();
const options = computed(() => {
  const flags: Record<
    number,
    {
      name: string;
      count: number;
      color?: string;
      abbreviationId: number;
      displayNameId: number;
    }
  > = filteredEvents.value.activeEvents
    .flatMap((event) => {
      if ("flags" in event) {
        return event.flags;
      }
      return [];
    })
    .reduce(
      (prev, curr) => {
        if (curr) {
          if (prev[curr.id] == null) {
            prev[curr.id] = {
              name: curr.name,
              count: 1,
              color: curr.color,
              displayNameId: curr.displayNameId,
              abbreviationId: curr.abbreviationId,
            };
          } else {
            prev[curr.id].count += 1;
          }
        }
        return prev;
      },
      {} as Record<
        number,
        {
          name: string;
          count: number;
          color?: string;
          abbreviationId: number;
          displayNameId: number;
        }
      >,
    );

  return Object.entries(flags)
    .map(([id, attr]) => ({
      id: Number(id),
      name: attr.name,
      count: attr.count,
      color: attr.color,
      abbreviationId: attr.abbreviationId,
      displayNameId: attr.displayNameId,
    }))
    .sort((lhs, rhs) => {
      return lhs.name.localeCompare(rhs.name);
    });
});
function hasOwnColor(option: OptionType) {
  if (option.color != null) {
    return {
      "background-color": option.color,
      color: getContrastColor(option.color),
    };
  }

  return { "background-color": "var(--p-surface-100)" };
}
const selected = ref<typeof options.value>([]);
function toggle() {
  multiselect.value?.hide?.();
}
function remove(option: OptionType) {
  selected.value = selected.value.filter((select) => select.id !== option.id);
}
function getFilter() {
  return new FlagsFilter(selected.value.map(({ id }) => id));
}

function applyFilter(filter: FlagsFilter) {
  selected.value = filter.flags.map((id) => {
    const option = options.value.find((v) => v.id === id);

    return {
      id,
      name: option?.name ?? "?",
      count: option?.count ?? 0,
      color: option?.color ?? "rgb(65, 184, 131)",
      abbreviationId: option?.abbreviationId ?? 0,
      displayNameId: option?.displayNameId ?? 0,
    };
  });
}
defineExpose({
  getFilter,
  applyFilter,
});

const { t, te } = useTranslation();
</script>

<style lang="scss" scoped>
span {
  margin-left: 0.2em;
}
</style>
